import { Component, ElementRef, OnDestroy, OnInit } from "@angular/core"
import { BsModalService } from "ngx-bootstrap/modal"
import { AppState, ShowRequest, TestbedServerJob } from "../../core"
import { CorrelationDashboardRecord } from "../../core/models/correlation-dashboard-record"
import { PostProcessingService, TestSeqImportJobCriteria } from "../../core/services"
import { DatePipe } from "@angular/common"
import { PathTestGroupCriteria } from "../../core/models/path-test"
import { StatMetric, TableViewComponentBase, TableViewQueryCriteria, TableViewFilter } from "../../shared/components/table-view/table-view-component-base"
import { TableRecActionOkResult, TableRecActionResult, TableRecArgs, TableRecsArgs, TableRecsAction, TableRecsActionOkResult, TableRecsActionResult, TableRecsParallelAction, TableRecsSequentialAction } from "../../shared/components/table-view/table-view-action"
import { RestClientResult } from "../../core/services/rest-client"
import { Subscription, firstValueFrom } from "rxjs"
import { MergedExcelConfirmationModalComponent } from "./merged-excel-confirmation-modal.component"
import { ExcelDateRangeModalComponent } from "./excel-date-range-modal.component"

type CorrelateKey = {
  stage:string;
  buildingid:string;
  routeid?:string;
  participant?:string;
  groupingfield?:string;
};

interface MergedExcelValidationResult {
  valid: boolean;
  errorMessage?: string;
  sharedCriteria?: {
    stage: string;
    buildingid: string;
    participant: string;
    routeid?: string;
  };
  groupingFields?: string[];
  recordCount?: number;
}

@Component({
  selector: 'app-correlation-dashboard-table',
  templateUrl: '../../shared/components/table-view/table-view.component.html',
  styleUrls: [
    '../../shared/components/table-view/scss/table-view.scss',
    '../../shared/components/table-view/scss/app-tab-full-height.scss',
    '../../shared/components/table-view/scss/fixed-headers-and-column.scss'
  ]
})
export class CorrelationDashboardTableComponent extends TableViewComponentBase<CorrelationDashboardRecord>
    implements OnInit, OnDestroy {

  webSocketMsgSubscription: Subscription;
  private browserInstanceId: string;

  constructor(
      appState: AppState,
      modalService: BsModalService,
      public postProcessingService: PostProcessingService,
      elementRef: ElementRef,
      datePipe: DatePipe
  ){
    super(appState,modalService,elementRef,datePipe)

    const stageColumn = { title:'Stage', name: 'stage', visible: true}
    const buildingIdColumn = { title:'Building ID', name: 'buildingid', visible: true}
    const routeIdColumn = { title:'Route ID', name: 'routeid', visible: true}
    const participantColumn = { title:'Participant', name: 'participant', visible: true}
    const groupingColumn = { title:'Grouping', name: 'groupingfield', visible: true}

    this.tableColumns  = [
      stageColumn,
      { title:'Market', name: 'market', visible: true},
      { title:'Morphology', name: 'morphology', visible: true},
      buildingIdColumn,
      { title:'Building Name', name: 'commonname', visible: true},
      routeIdColumn,
      { title:'Start Time (UTC)', name: 'starttime', visible: true, template: 'time'},
      { title:'End Time (UTC)', name: 'endtime', visible: true, template: 'time'},
      { title:'Seqs', name: 'seqcnt', visible: true, template: 'number'},
      participantColumn,
      groupingColumn,
      { title:'Calls', name: 'calls', visible: true, template: 'number'},
      { title:'Calls in Correla', name: 'callsincorrelation', visible: true, template: 'number'},
      { title:'Correlated Calls', name: 'correlatedcalls', visible: true, template: 'number'},
      { title:'Correlated Calls %', name: 'correlatedcallspct', visible: true, template: 'percent'},
      { title:'Correlated At (Local)', name: 'lastcorrbuild', visible: true, template: 'localtime'},
      { title:'Actions', name: 'actions', visible: true, sortable: false, filterable: false, template: 'actions',
        headerCellCssClass: 'bulk-actions', dataCellCssClass: 'bulk-actions', filterCellCssClass: 'bulk-actions'}
    ]

    this.setRowActions([
      this.correlateAction = new TableRecsParallelAction<CorrelationDashboardRecord>('Correlate', this.triggerImportJob).setMaxConcurrent(3).setOnComplete(args => this.load()),
      new TableRecsSequentialAction<CorrelationDashboardRecord>('Results Search', this.showCorrelation ).setBulkable(true),
      new TableRecsSequentialAction<CorrelationDashboardRecord>('Results Summary', this.showCorrelationSummary ).setBulkable(true),
      new TableRecsSequentialAction<CorrelationDashboardRecord>('Testing Summary', this.showBuildingStage ).setBulkable(true),
      this.excelAction = new TableRecsParallelAction<CorrelationDashboardRecord>('Excel', this.correlationResultsReportXLWbkAsync )
        .setMaxConcurrent(3),
      this.customExcelAction = new TableRecsParallelAction<CorrelationDashboardRecord>('Excel - Custom', this.correlationResultsReportXLWbkAsync)
        .setMaxConcurrent(3)
        .setOnBegin(this.promptExcelDateRange) as TableRecsParallelAction<CorrelationDashboardRecord>,
    ])

    // Remove per-row Excel and Excel - Custom from bulk actions, replace with bulk-only version that prompts for date range
    this.bulkRowActions = this.bulkRowActions.filter(a => a !== this.excelAction && a !== this.customExcelAction);
    this.bulkExcelAction = new TableRecsParallelAction<CorrelationDashboardRecord>('Excel - Custom', this.correlationResultsReportXLWbkAsync)
      .setMaxConcurrent(3)
      .setOnBegin(this.promptExcelDateRange) as TableRecsParallelAction<CorrelationDashboardRecord>;
    this.bulkRowActions.push(this.bulkExcelAction);

    // Add merged Excel action only to bulk actions (not to single row actions)
    this.mergedExcelAction = new TableRecsAction<CorrelationDashboardRecord>('Excel - Multiple Groupings', this.downloadMergedExcelReportBulk)
      .setBulkable(true)
      .setOnBegin(this.validateAndConfirmMergedExcel);
    this.bulkRowActions.push(this.mergedExcelAction);
    this.idColumns = [ buildingIdColumn, stageColumn, participantColumn, groupingColumn, routeIdColumn ]
    this.bStickyBulkActions = true

    const metrics: StatMetric[] = [
      { column: "calls", statTypes: ["min", "max", "avg"] },
      { column: "callsincorrelation", statTypes: ["min", "max", "avg"] },
      { column: "correlatedcalls", statTypes: ["min", "max", "avg"] },
      //{ column: "correlatedcallspct", statTypes: ["min", "max", "avg"] },
      {
        column: "starttime",
        statTypes: ["min", "max"],
        format: { dataType: "date", layout: "short" },
      },
      { column: "*", format: { dataType: "number" }, statTypes: ["count"] },
    ];
    this.statGroups = [
      {
        id: "byParticipant",
        groupBy: ["participant",],
        visible: true,
        metrics,
      },
      {
        id: "byGroupingParticipant",
        groupBy: ["groupingfield", "participant",],
        visible: false,
        metrics,
      },
      {
        id: "byBuilding",
        groupBy: ["market","buildingid"],
        metrics,
      },      
      {
        id: "byStage",
        groupBy: ["stage"],
        metrics,
      },      
    ];

    // Initialize browser instance ID for job filtering
    this.browserInstanceId = this.getBrowserInstanceId();
  }

  private getBrowserInstanceId(): string {
    const storageKey = 'testbed-browser-instance-id';
    let instanceId = localStorage.getItem(storageKey);
    
    if (!instanceId) {
      // Generate a new GUID
      instanceId = 'browser_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36);
      localStorage.setItem(storageKey, instanceId);
    }
    
    return instanceId;
  }

  private isJobFromThisBrowser(job: TestbedServerJob): boolean {
    return job?.key?.originatingBrowserId === this.browserInstanceId;
  }

  private updateExcelActionTitle(): void {
    const myJobs = this.excelJobs.filter(job => this.isJobFromThisBrowser(job));
    const otherJobs = this.excelJobs.filter(job => !this.isJobFromThisBrowser(job));
    
    if (myJobs.length > 0) {
      this.excelAction.title = 'Downloading';
    } else if (otherJobs.length > 0) {
      this.excelAction.title = 'Busy';
    } else {
      this.excelAction.title = 'Excel';
    }
  }
  correlateAction: TableRecsParallelAction<CorrelationDashboardRecord>;
  excelAction: TableRecsParallelAction<CorrelationDashboardRecord>;
  customExcelAction: TableRecsParallelAction<CorrelationDashboardRecord>;
  bulkExcelAction: TableRecsParallelAction<CorrelationDashboardRecord>;
  mergedExcelAction: TableRecsAction<CorrelationDashboardRecord>;

  private excelStartDate = '';
  private excelEndDate = '';

  promptExcelDateRange = async (args: TableRecsArgs<CorrelationDashboardRecord>): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      let confirmed = false;

      const initialState = {
        onConfirm: (startDate: string, endDate: string) => {
          confirmed = true;
          this.excelStartDate = startDate;
          this.excelEndDate = endDate;
          resolve(true);
        }
      };

      const modalRef = this.modalService.show(ExcelDateRangeModalComponent, {
        initialState,
        ignoreBackdropClick: true,
      });

      modalRef.onHide.subscribe(() => {
        if (!confirmed) {
          resolve(false);
        }
      });
    });
  }

  triggerImportJob = async ({rec}:TableRecArgs<CorrelationDashboardRecord>) => {
    const {filters} = this.queryCriteriaForRecord(rec)
    const jobArgs: TestSeqImportJobCriteria = {}
    filters.forEach(f=>jobArgs[f.key]=f.value)

    try {
      console.log(JSON.stringify({ data: jobArgs}, null, 4))
      const resp = await this.postProcessingService.runTestSeqImportJob(jobArgs)
      this.appState.notifyInfo(`[${jobArgs.participant||''} ${jobArgs.buildingid||''} ${jobArgs.stage||''} ${jobArgs.routeid||''}] ${resp.msg}` ,this,5)

      // rebuild done, now update UI table

      // composite key for UI table (ensure UI row gets updated by specific db record)
      const rowCriteriaList = [{
        stage: rec.stage,
        buildingid: rec.buildingid,
        participant: rec.participant,
        groupingfield: rec.groupingfield,
        routeid: rec.routeid
      }]

      const params = { 
        alternateViewName: this.selectedAlternateViewName, 
        rowCriteria: rowCriteriaList.map( criteria => JSON.stringify(criteria) ),
        sorts: JSON.stringify(this.sortColumns) // sort to match UI for easier to debugging
      };
      const httpClient = this.postProcessingService.httpClient
      const httpResp = await firstValueFrom(httpClient.http.get<RestClientResult<CorrelationDashboardRecord[]>>(
        `${httpClient.urlPath}/correlationDashboard/findRows`, { params, observe: 'response' }));
      const result = httpResp.body
      const rtnRecs = result.data
      this.updateRows(rtnRecs)
      return new TableRecActionOkResult(rec)
    } catch( error ) {
      console.error(error)
      const errorMsg = `Error occured running import (${JSON.stringify(jobArgs)}). ${this.postProcessingService.extractErrorMsg(error)}`
      this.reportErrorFn(error)
      return new TableRecActionResult(rec,false,errorMsg)
    }
  }

  showBuildingStage = async ({rec}:TableRecArgs<CorrelationDashboardRecord>) => {
    const groupCriteria: PathTestGroupCriteria = { stage: rec.stage, buildingId: rec.buildingid }
    const strGroupFilter = JSON.stringify( groupCriteria )
    this.appState.showView.next( new ShowRequest('tab', 'path test group', strGroupFilter, null) )
    return new TableRecActionOkResult(rec)
  }

  showCorrelationSummary = async ({rec}:TableRecArgs<CorrelationDashboardRecord>) => {
    const context = this.queryCriteriaForRecord(rec)
    if ( !rec.groupingfield ) {
      context.filters.push({key: 'groupingfield', mode: '~*', value: "^$" }) // want only rows with no grouping field
    }
    const strContext = JSON.stringify(context)
    this.appState.showView.next( new ShowRequest('tab', 'correlation summary', strContext, null) )
    return new TableRecActionOkResult(rec)
  }

  showCorrelation = async ({rec}:TableRecArgs<CorrelationDashboardRecord>) => {
    console.log('showCorrelation() started ' + rec)
    const context = this.queryCriteriaForRecord(rec)
    const strContext = JSON.stringify(context)
    this.appState.showView.next( new ShowRequest('tab', 'correlation', strContext, null) )
    console.log('showCorrelation() finished ' + rec)
    return new TableRecActionOkResult(rec)
  }

  correlationResultsReportXLWbkAsync = async ({rec}:TableRecArgs<CorrelationDashboardRecord>) => {
    const {filters} = this.queryCriteriaForRecord(rec)
    try {
      // Immediately add to running state for instant button feedback
      const runningRecs = this.excelAction.running$.value;
      if (!runningRecs.find(r=>r===rec)) {
        this.excelAction.running$.next([...runningRecs, rec]);
      }
      
      const participant = filters.find(f=>f.key=='participant')?.value
      const suffix = ' (' + rec.starttime.toString().split('T')[0] + ')'
      
      if (participant=='DISH') {
        // Start both DISH jobs
        await this.postProcessingService.runResultsWorkbookJob(filters, suffix, true, this.browserInstanceId, this.excelStartDate, this.excelEndDate);
        await this.postProcessingService.runResultsWorkbookJob(filters, suffix, false, this.browserInstanceId, this.excelStartDate, this.excelEndDate);

        this.appState.notifyInfo(`[${participant} ${rec.buildingid} ${rec.stage}] Excel report generation started (2 reports)`, this, 5)
      } else {
        // Start single job
        await this.postProcessingService.runResultsWorkbookJob(filters, suffix, undefined, this.browserInstanceId, this.excelStartDate, this.excelEndDate);
        this.appState.notifyInfo(`[${participant} ${rec.buildingid} ${rec.stage}] Excel report generation started`, this, 5)
      }
      
      return new TableRecActionOkResult(rec)
    } catch( error ) {
      console.error(error)
      // Remove from running state on error
      const runningRecs = this.excelAction.running$.value;
      this.excelAction.running$.next(runningRecs.filter(r=>r!==rec));
      
      const errorMsg = `Error starting Excel Results Workbook generation (${filters.map(f=>f.key+f.mode+f.value)}). ${this.postProcessingService.extractErrorMsg(error)}`
      this.reportErrorFn(error)
      return new TableRecActionResult(rec,false,errorMsg)
    }
  }

  queryCriteriaForRecord(record:CorrelationDashboardRecord){
    const criteria : TableViewQueryCriteria = {
      filters : []
    }
    if ( record.stage ) {
      criteria.filters.push({key: 'stage', mode: '=', value: record.stage })
    }
    if ( record.participant ) {
      criteria.filters.push({key: 'participant',  mode: '=', value: record.participant })
    }
    if ( record.buildingid ) {
      criteria.filters.push({key: 'buildingid', mode: '=', value: record.buildingid })
    }
    if ( record.groupingfield ) {
      criteria.filters.push({key: 'groupingfield', mode: '=', value: record.groupingfield })
    }
    if ( record.routeid ) {
      criteria.filters.push({key: 'routeid', mode: '=', value: record.routeid })
    }
    criteria['selectedViewName'] = 'testingContext';
    return criteria
  }

  validateRecordsForMerge(records: CorrelationDashboardRecord[]): MergedExcelValidationResult {
    if (!records || records.length === 0) {
      return { valid: false, errorMessage: 'No records selected' };
    }

    const ref = records[0];

    // Check all records match shared criteria
    for (const rec of records) {
      if (rec.stage !== ref.stage) {
        return { valid: false, errorMessage: 'Cannot merge records from different stages. All selected records must have the same stage.' };
      }
      if (rec.buildingid !== ref.buildingid) {
        return { valid: false, errorMessage: 'Cannot merge records from different buildings. All selected records must have the same building ID.' };
      }
      if (rec.participant !== ref.participant) {
        return { valid: false, errorMessage: 'Cannot merge records from different participants. All selected records must have the same participant.' };
      }
      if (rec.routeid !== ref.routeid) {
        return { valid: false, errorMessage: 'Cannot merge records from different routes. All selected records must have the same route ID (or all must be building tests).' };
      }
    }

    // Sort records by starttime, then extract unique grouping fields
    const sortedRecords = [...records].sort((a, b) => {
      const timeA = new Date(a.starttime).getTime();
      const timeB = new Date(b.starttime).getTime();
      return timeA - timeB;
    });

    const groupingFields = [...new Set(
      sortedRecords.map(r => r.groupingfield).filter(g => g && g.trim())
    )];

    if (groupingFields.length === 0) {
      return { valid: false, errorMessage: 'Selected records have no grouping fields to merge.' };
    }

    // Block if all records have exact same grouping field
    if (groupingFields.length === 1 && records.length > 1) {
      return {
        valid: false,
        errorMessage: 'All selected records have the same grouping field. Please use the regular Excel action instead.'
      };
    }

    return {
      valid: true,
      sharedCriteria: {
        stage: ref.stage,
        buildingid: ref.buildingid,
        participant: ref.participant,
        routeid: ref.routeid
      },
      groupingFields,
      recordCount: records.length
    };
  }

  validateAndConfirmMergedExcel = async (args: TableRecsArgs<CorrelationDashboardRecord>): Promise<boolean> => {
    const validation = this.validateRecordsForMerge(args.recs);

    if (!validation.valid) {
      this.appState.notifyError(validation.errorMessage, this, 5);
      return false;
    }

    return new Promise<boolean>((resolve) => {
      let confirmed = false;

      const initialState = {
        sharedCriteria: validation.sharedCriteria,
        groupingFields: validation.groupingFields,
        recordCount: validation.recordCount,
        onConfirm: async (startDate: string, endDate: string) => {
          confirmed = true;
          this.excelStartDate = startDate;
          this.excelEndDate = endDate;
          resolve(true);
        }
      };

      const modalRef = this.modalService.show(MergedExcelConfirmationModalComponent, {
        initialState,
        ignoreBackdropClick: true,
        class: 'modal-lg'
      });

      // Resolve to false if user closes modal without confirming
      modalRef.onHide.subscribe(() => {
        if (!confirmed) {
          resolve(false);
        }
      });
    });
  }

  buildMergedFilters(records: CorrelationDashboardRecord[]): TableViewFilter[] {
    const ref = records[0];
    const filters: TableViewFilter[] = [];

    // Add shared criteria filters
    filters.push({key: 'stage', mode: '=', value: ref.stage});
    filters.push({key: 'buildingid', mode: '=', value: ref.buildingid});
    filters.push({key: 'participant', mode: '=', value: ref.participant});

    if (ref.routeid) {
      filters.push({key: 'routeid', mode: '=', value: ref.routeid});
    }

    // Sort records by starttime, then extract unique grouping fields
    const sortedRecords = [...records].sort((a, b) => {
      const timeA = new Date(a.starttime).getTime();
      const timeB = new Date(b.starttime).getTime();
      return timeA - timeB;
    });

    const groupingFields = [...new Set(
      sortedRecords.map(r => r.groupingfield).filter(g => g && g.trim())
    )];

    if (groupingFields.length > 0) {
      filters.push({
        key: 'groupingfield',
        mode: '=',
        value: groupingFields.join(',')
      });
    }

    return filters;
  }

  downloadMergedExcelReportBulk = async (args: TableRecsArgs<CorrelationDashboardRecord>) => {
    const {recs} = args;

    // Mark all selected records as running immediately
    this.mergedExcelAction.running$.next([...recs]);

    const filters = this.buildMergedFilters(recs);
    const groupingFieldsCount = recs
      .map(r => r.groupingfield)
      .filter(g => g && g.trim())
      .filter((g, i, arr) => arr.indexOf(g) === i)
      .length;

    try {
      await this.postProcessingService.runResultsWorkbookJob(
        filters,
        '', // Empty suffix - use server's filename
        undefined,
        this.browserInstanceId,
        this.excelStartDate,
        this.excelEndDate
      );

      this.appState.notifyInfo(
        `Excel report generation started for ${groupingFieldsCount} grouping field(s)`,
        this,
        5
      );

      const results = recs.map(rec => new TableRecActionOkResult(rec));
      return new TableRecsActionOkResult('Merged Excel report started', results);
    } catch (error) {
      // Clear running state on error
      this.mergedExcelAction.running$.next([]);

      const errorMsg = `Error starting merged Excel report: ${this.postProcessingService.extractErrorMsg(error)}`;
      this.reportErrorFn(error);

      const results = recs.map(rec =>
        new TableRecActionResult(rec, false, errorMsg)
      );
      return new TableRecsActionResult(false, errorMsg, results);
    }
  }


  bRebuilding = false
  async rebuildAll() {
    console.log(`rebuildAll()`)
    this.errorMsg = null
    this.bRebuilding = true

    try {
      await this.postProcessingService.rebuildCorrelationDashboard()
    } catch ( error : any ) {
      this.errorMsg = `Dashboard rebuild error: ${this.postProcessingService.extractErrorMsg(error)}`
      this.appState.publishErrorObj(error,this.errorMsg,this)
    } finally {
      this.bRebuilding = false
      await this.load()
    }
  }

  correlatingJobs: TestbedServerJob[] = [];
  excelJobs: TestbedServerJob[] = [];

  ngOnInit(): void {
    this.init();

    // listen for correlation and dashboard rebuild jobs on server
    this.webSocketMsgSubscription = this.appState.webSocketMsg$.subscribe({
      next: (msg: {type: string, data: any}) => {
        
        const findByCorrelationKey = (recs:any[],{stage,buildingid,routeid,participant,groupingfield}: CorrelateKey) =>
          recs.find(r=>r.stage==stage&&r.buildingid==buildingid&&
          r.routeid==routeid&&r.participant==participant&&r.groupingfield==groupingfield);

        const findByExcelJobKey = (recs:any[], jobKey: any) => {
          // Excel jobs have extended key with userId and jobId, but we match on correlation params
          return recs.find(r=>r.stage==jobKey.stage&&r.buildingid==jobKey.buildingid&&
            r.routeid==jobKey.routeid&&r.participant==jobKey.participant&&r.groupingfield==jobKey.groupingfield);
        };

        const findRecordsForMergedJob = (recs:any[], jobKey: any): any[] => {
          // Check if this is a merged job (groupingfield contains comma)
          if (!jobKey.groupingfield || !jobKey.groupingfield.includes(',')) {
            return [];
          }

          // Split comma-separated grouping fields
          const groupingFields = jobKey.groupingfield.split(',').map(g => g.trim());

          // Find all records that match the base criteria and have one of the grouping fields
          return recs.filter(r =>
            r.stage === jobKey.stage &&
            r.buildingid === jobKey.buildingid &&
            (r.routeid || null) === (jobKey.routeid || null) &&
            r.participant === jobKey.participant &&
            groupingFields.includes(r.groupingfield)
          );
        };

        switch (msg.type) {
          case 'activeJobs': {
            const jobs: TestbedServerJob[] = msg.data;
            console.log({jobs});
            this.correlatingJobs = jobs.filter(j=>j.type==='correlate');
            this.excelJobs = jobs.filter(j=>j.type==='reportGeneration');
            
            const correlatingRecs = this.correlatingJobs.map(job=>
              findByCorrelationKey(this.records,job.key)).filter(r=>r);
            this.correlateAction.running$.next(correlatingRecs);
            
            const excelRecs = this.excelJobs.map(job=>
              findByExcelJobKey(this.records,job.key)).filter(r=>r);
            this.excelAction.running$.next(excelRecs);
            this.customExcelAction.running$.next(excelRecs);
            this.bulkExcelAction.running$.next(excelRecs);

            // Handle merged Excel jobs
            const mergedExcelRecs: CorrelationDashboardRecord[] = [];
            for (const job of this.excelJobs) {
              const mergedRecs = findRecordsForMergedJob(this.records, job.key);
              if (mergedRecs.length > 0) {
                mergedExcelRecs.push(...mergedRecs);
              }
            }
            this.mergedExcelAction.running$.next(mergedExcelRecs);

            // Update Excel button title based on job status
            this.updateExcelActionTitle();
            break;
          }
          case 'jobEvent': {
            const job: TestbedServerJob = msg.data;
            if ( job.type == 'correlate') {
              const rec = findByCorrelationKey(this.records, job.key);
              if ( rec ) {
                if ( job.status.value === 'running' ) {
                  if (!findByCorrelationKey(this.correlateAction.running$.value,job.key) ){
                    this.correlateAction.running$.next([...this.correlateAction.running$.value,rec]);
                  }
                } else if ( job.status.value !== 'initialized' ) {
                  const runningRecs = this.correlateAction.running$.value;
                  this.correlateAction.running$.next(runningRecs.filter(r=>r!=rec));
                }
              }

              // update active correlate job count in table header
              if (job.status.value === 'running' ) {
                const bFoundCorrelatingJob = findByCorrelationKey(this.correlatingJobs.map(j=>j.key),job.key);
                if ( !bFoundCorrelatingJob ){
                  this.correlatingJobs=[...this.correlatingJobs,job];
                }
              } else if ( job.status.value !== 'initialized' ) {
                this.correlatingJobs=this.correlatingJobs.filter(j=>!findByCorrelationKey([j.key],job.key));
              }
            } else if ( job.type == 'reportGeneration') {
              // Check if this is a merged job
              const mergedRecs = findRecordsForMergedJob(this.records, job.key);
              const isMergedJob = mergedRecs.length > 0;

              if (isMergedJob) {
                // Handle merged Excel job
                if ( job.status.value === 'running' ) {
                  // Add all related records to running state
                  const currentRunning = this.mergedExcelAction.running$.value;
                  const newRunning = [...currentRunning];
                  for (const rec of mergedRecs) {
                    if (!newRunning.find(r => r === rec)) {
                      newRunning.push(rec);
                    }
                  }
                  this.mergedExcelAction.running$.next(newRunning);

                  // Show progress updates as info notifications
                  if (job.status.description) {
                    this.appState.notifyInfo(`${job.status.description}`, this, 5);
                  }
                } else if ( job.status.value === 'complete' ) {
                  // Auto-download the completed report only if this browser initiated the job
                  if (job.downloadUrl && job.key?.originatingBrowserId === this.browserInstanceId) {
                    const jobId = job.downloadUrl.split('/').pop();
                    // Use the merged filters for download
                    const filters = this.buildMergedFilters(mergedRecs);
                    this.postProcessingService.downloadCompletedReport(jobId, filters, ''); // Empty suffix - use server's filename
                  }

                  // Remove all related records from running state
                  const runningRecs = this.mergedExcelAction.running$.value;
                  this.mergedExcelAction.running$.next(
                    runningRecs.filter(r => !mergedRecs.find(mr => mr === r))
                  );
                } else if ( job.status.value === 'error' ) {
                  // Remove all related records from running state
                  const runningRecs = this.mergedExcelAction.running$.value;
                  this.mergedExcelAction.running$.next(
                    runningRecs.filter(r => !mergedRecs.find(mr => mr === r))
                  );
                  this.appState.notifyError(`Merged Excel report generation failed: ${job.status.description}`, this, 10);
                }
              } else {
                // Handle regular (non-merged) Excel job
                const rec = findByExcelJobKey(this.records, job.key);
                if ( rec ) {
                  if ( job.status.value === 'running' ) {
                    console.log('Excel job progress:', job.status.description);
                    if (!findByExcelJobKey(this.excelAction.running$.value,job.key) ){
                      this.excelAction.running$.next([...this.excelAction.running$.value,rec]);
                      this.customExcelAction.running$.next([...this.customExcelAction.running$.value,rec]);
                      this.bulkExcelAction.running$.next([...this.bulkExcelAction.running$.value,rec]);
                    }
                    // Show progress updates as info notifications (optional)
                    if (job.status.description) {
                      this.appState.notifyInfo(`${job.status.description}`, this, 5);
                    }
                  } else if ( job.status.value === 'complete' ) {
                    // Auto-download the completed report only if this browser initiated the job
                    if (job.downloadUrl && job.key?.originatingBrowserId === this.browserInstanceId) {
                      const filters = this.queryCriteriaForRecord(rec).filters;
                      const suffix = ' (' + rec.starttime.toString().split('T')[0] + ')';
                      const jobId = job.downloadUrl.split('/').pop();
                      this.postProcessingService.downloadCompletedReport(jobId, filters, suffix);
                    }

                    const runningRecs = this.excelAction.running$.value;
                    this.excelAction.running$.next(runningRecs.filter(r=>r!=rec));
                    const customRunningRecs = this.customExcelAction.running$.value;
                    this.customExcelAction.running$.next(customRunningRecs.filter(r=>r!=rec));
                    const bulkRunningRecs = this.bulkExcelAction.running$.value;
                    this.bulkExcelAction.running$.next(bulkRunningRecs.filter(r=>r!=rec));
                  } else if ( job.status.value === 'error' ) {
                    const runningRecs = this.excelAction.running$.value;
                    this.excelAction.running$.next(runningRecs.filter(r=>r!=rec));
                    const customRunningRecsErr = this.customExcelAction.running$.value;
                    this.customExcelAction.running$.next(customRunningRecsErr.filter(r=>r!=rec));
                    const bulkRunningRecsErr = this.bulkExcelAction.running$.value;
                    this.bulkExcelAction.running$.next(bulkRunningRecsErr.filter(r=>r!=rec));
                    this.appState.notifyError(`Excel report generation failed: ${job.status.description}`, this, 10);
                  }
                }
              }

              // update active excel job count in table header
              if (job.status.value === 'running' ) {
                const bFoundExcelJob = this.excelJobs.find(j => j.key.jobId === job.key.jobId);
                if ( !bFoundExcelJob ){
                  this.excelJobs=[...this.excelJobs,job];
                }
              } else if ( job.status.value !== 'initialized' ) {
                this.excelJobs=this.excelJobs.filter(j => j.key.jobId !== job.key.jobId);
              }
              
              // Update Excel button title when job status changes
              this.updateExcelActionTitle();
            } else if ( job.type == 'rebuildCorrelationDashboard') {
              console.log('TODO update rebuild button',job)
            }
            break;
          }
        }
      }
    });

  }

  ngOnDestroy(): void {
    this.destroy()
    this.webSocketMsgSubscription?.unsubscribe();
  }


  doQuery = async (queryFilter) => {
    const result = await this.postProcessingService.findCorrelationDashboardRecords(queryFilter);
    return result;
  }


  getName = () => 'correlation-dashboard'

}
